import { useState } from "react";
import { useDispatch } from "react-redux";
import Chip from "../../../components/chip";
import { StackOutlined } from "../../../components/icons/stack-outlined";
import { TagFilled } from "../../../components/icons/tag-filled";
import { TimeOutlined } from "../../../components/icons/time-outlined";
import { timeFormatter } from "../../../core/utils";
import {
  deleteReportStateAction,
  ReportProgress,
  ReportResponse,
} from "../../../redux/features/report-slice";
import { CopyOutlined } from "../../../components/icons/copy-outlined";
import { TrashOutlined } from "../../../components/icons/trash-outlined";
import { AppDispatch } from "../../../redux/store";

interface ItemDailyReportProps {
  report: ReportResponse;
  onClick: () => void;
  onCopy: (() => void) | null;
}

export default function ItemDailyReport(props: ItemDailyReportProps) {
  const { report, onClick, onCopy } = props;

  const dispatch = useDispatch<AppDispatch>();

  const [isHover, setIsHover] = useState<boolean>(false);

  const orgName = report.taskGroup.taskFunction.organization.name;

  const orgColor =
    orgName === "Head Office"
      ? "#7d003c"
      : orgName === "HCM Office"
      ? "#0e7ab0"
      : "#a37402";

  const submitDeleteReport = async () => {
    if (report) {
      await dispatch(deleteReportStateAction({ id: report.id }));
    }
  };

  return (
    <div
      key={report.id}
      className="bg-background-paper relative rounded-lg flex flex-col shadow p-2 mb-2 hover:bg-gray-50 cursor-pointer border border-divider"
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <label className="font-semibold text-md">{report.taskGroup.title}</label>
      {report.startTime && report.endTime && (
        <label className="text-xs text-text-title font-normal">
          {`${timeFormatter(new Date(report.startTime))} - ${timeFormatter(
            new Date(report.endTime)
          )}`}
        </label>
      )}
      <div className="flex items-center space-x-1 mt-2">
        <label
          className="text-xs whitespace-nowrap font-medium"
          style={{ color: orgColor }}
        >
          {report.taskGroup.taskFunction.organization.name}
        </label>
        <label>•</label>
        <label className="text-xs line-clamp-1">
          {report.taskGroup.taskFunction.title}
        </label>
        {report.taskGroup.branch !== null && (
          <label className="text-xs whitespace-nowrap">
            {`/ ${report.taskGroup.branch.name}`}
          </label>
        )}
      </div>
      <div className="text-xs line-clamp-3 text-text-title border-t border-divider border-dashed pt-1 mt-1">
        {report.detail === "" ? "-" : report.detail}
      </div>
      <div className="flex items-center justify-between overlow-x-hidden mt-2">
        <Chip
          label={report.progress}
          exClassName={
            report.progress === ReportProgress.None
              ? "text-gray-500 border-gray-500"
              : report.progress === ReportProgress.Pending
              ? "text-error-main border-error-main"
              : report.progress === ReportProgress.Inprogress
              ? "text-warning-main border-warning-main"
              : "text-success-main border-success-main"
          }
        />
        <div className="flex items-center justify-end space-x-4 overlow-x-hidden">
          {report.tag && (
            <TagFilled
              className="w-4 h-4"
              style={{
                color: report.tag.split("-")[1],
              }}
            />
          )}
          {report.overtime && (
            <label className="text-sm text-text-title">OT</label>
          )}
          <label
            title="Duration"
            className="w-fit flex items-center text-xs space-x-1 text-text-title whitespace-nowrap"
          >
            <TimeOutlined className="w-4 h-4 text-text-title" />
            <label>{report.duration}</label>
          </label>
          <label
            title="Quantity"
            className="w-fit flex items-center text-xs space-x-1 text-text-title whitespace-nowrap"
          >
            <StackOutlined className="w-4 h-4 text-text-title" />
            <label>{report.quantity}</label>
          </label>
        </div>
      </div>
      {isHover && onCopy && (
        <div className="absolute flex items-center right-1 top-1 border border-divider rounded-lg p-1 space-x-1">
          {/* {report.progress !== ReportProgress.Completed && (
            <label
              title="Mark complete"
              className="rounded hover:bg-gray-200 cursor-pointer p-1"
            >
              <CheckOutlined className="h-4 w-4" />
            </label>
          )} */}
          <label
            title="Copy"
            className="rounded hover:bg-gray-200 cursor-pointer p-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCopy();
            }}
          >
            <CopyOutlined className="h-4 w-4" />
          </label>
          <label
            title="Delete"
            className="rounded hover:bg-gray-200 cursor-pointer p-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              submitDeleteReport();
            }}
          >
            <TrashOutlined className="h-4 w-4 text-error-main" />
          </label>
        </div>
      )}
    </div>
  );
}
